import React from "react";
import { useLocation } from "react-router-dom";

import { Breadcrumb, Breadcrumbs as PyBreadcrumbs } from "@performyard/py-components";

import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";

import { REVIEW_CYCLES_LIST_HREF, parseReviewsOrigin, parseViaReview } from "../../../utils/reviewsBreadcrumbs";

type FormAuthoringBreadcrumbsProps = {
	formName: string;
	reviewId: string;
	reviewName: string;
	cycleName: string;
	canViewReviewCycles: boolean;
};

export const FormAuthoringBreadcrumbs = ({
	formName,
	reviewId,
	reviewName,
	cycleName,
	canViewReviewCycles,
}: FormAuthoringBreadcrumbsProps): React.ReactElement | null => {
	const isNewNav = useIsNewNavigation();
	const location = useLocation();

	if (!isNewNav) return null;
	if (!formName) return null;

	const { from, label, kind } = parseReviewsOrigin(location.search);
	const viaReview = parseViaReview(location.search);

	// The form is viewed inside a review, so the parent review crumb appears for
	// every origin that passes through the review detail page (review + cycle).
	// Carry the origin forward so the review page also roots on where the user came from.
	const reviewHref = from
		? `/view-review/${reviewId}/?from=${encodeURIComponent(from)}`
		: `/view-review/${reviewId}/`;
	const reviewCrumb: Breadcrumb | null =
		reviewId && reviewName
			? { pageId: "review-detail", name: reviewName, href: reviewHref, forceHardReload: true }
			: null;

	const formCrumb: Breadcrumb = { pageId: "form-authoring", name: formName, href: location.pathname };

	let items: Breadcrumb[];
	if (kind === "cycle") {
		items = [
			...(canViewReviewCycles
				? [
						{
							pageId: "review-cycles",
							name: "Review Cycles",
							href: REVIEW_CYCLES_LIST_HREF,
							forceHardReload: true,
						},
					]
				: []),
			{ pageId: "review-cycle-details", name: cycleName || "Review Cycle", href: from, forceHardReload: true },
			...(reviewCrumb ? [reviewCrumb] : []),
			formCrumb,
		];
	} else if (kind === "review" && label) {
		items = [
			{ pageId: "reviews-parent", name: label, href: from, forceHardReload: true },
			...(reviewCrumb ? [reviewCrumb] : []),
			formCrumb,
		];
	} else if (kind === "direct" && label) {
		items = [
			{ pageId: "reviews-parent", name: label, href: from, forceHardReload: true },
			...(viaReview && reviewCrumb ? [reviewCrumb] : []),
			formCrumb,
		];
	} else {
		// Unknown / no origin: root on the parent review when we know it.
		items = [...(reviewCrumb ? [reviewCrumb] : []), formCrumb];
	}

	return <PyBreadcrumbs items={items} dangerouslySetClassName="form-authoring-breadcrumbs-nav" />;
};
